import type { CSSProperties, ReactNode, Ref } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import classNames from "classnames";
import type { ColumnPin } from "@common/types/views/views";
import { Button } from "@design-system/Button/Button";
import { DragHandle6DotIcon, PinIcon } from "@icons";
import { useColumnReorderTransition } from "./columnSettingsMotion";
import { PinMenu } from "./PinMenu";
import { VisibilityButton } from "./VisibilityButton";
import styles from "./ColumnItem.module.scss";

interface ColumnRowContentProps {
    name: string;
    /** A note under the name. */
    hint?: string;
    isVisible: boolean;
    isPinned?: ColumnPin;
    /** Can't be hidden. */
    isLocked: boolean;
    /** Can't be moved or pinned; a pinned one shows a static pin instead of the pin menu. */
    isImmutable: boolean;
    onToggleVisibility?: () => void;
    onPin?: (pin: ColumnPin | undefined) => void;
}

interface ColumnRowProps extends ColumnRowContentProps {
    /** The drag handle, first of the trailing controls. */
    handle?: ReactNode;
    className?: string;
    ref?: Ref<HTMLDivElement>;
    style?: CSSProperties;
}

const ColumnRow = ({
    name,
    hint,
    isVisible,
    isPinned,
    isLocked,
    isImmutable,
    onToggleVisibility,
    onPin,
    handle,
    className,
    ref,
    style,
}: ColumnRowProps) => (
    <div ref={ref} style={style} className={classNames(styles.row, className)}>
        <span className={styles.label}>
            <span
                className={classNames(styles.name, {
                    [styles.hiddenName]: !isVisible,
                })}
            >
                {name}
            </span>
            {hint && <span className={styles.hint}>{hint}</span>}
        </span>
        <div className={styles.trailing}>
            {handle}
            {isVisible && onPin && !isImmutable && (
                <span
                    className={styles.pinSlot}
                    data-pinned={isPinned === undefined ? undefined : ""}
                >
                    <PinMenu name={name} isPinned={isPinned} onPin={onPin} />
                </span>
            )}
            {isImmutable && isPinned !== undefined && (
                <span
                    className={styles.pinnedMark}
                    role="img"
                    aria-label={`${name} column is pinned`}
                >
                    <PinIcon />
                </span>
            )}
            {onToggleVisibility && (
                <VisibilityButton
                    name={name}
                    isVisible={isVisible}
                    isLocked={isLocked}
                    onToggleVisibility={onToggleVisibility}
                />
            )}
        </div>
    </div>
);

interface ColumnItemProps {
    readonly id: string;
    readonly name: string;
    /** A note under the name. */
    readonly hint?: string;
    readonly isVisible: boolean;
    readonly isPinned?: ColumnPin;
    readonly immutable?: boolean;
    readonly required?: boolean;
    readonly onToggleVisibility?: () => void;
    /** With it, a shown row gets the pin menu. */
    readonly onPin?: (pin: ColumnPin | undefined) => void;
    readonly isDraggable?: boolean;
    /** Some row is being dragged, so the others don't react to the pointer. */
    readonly isDragInProgress?: boolean;
}

/**
 * The row's drag handle. The list row passes its sortable bindings; the
 * lifted copy in the drag overlay renders the same button without them.
 */
const ColumnDragHandle = ({
    name,
    sortable,
}: Readonly<{
    name: string;
    sortable?: Pick<ReturnType<typeof useSortable>, "attributes" | "listeners">;
}>) => (
    <Button
        {...sortable?.attributes}
        {...sortable?.listeners}
        variant="secondary"
        size="sm"
        iconOnly
        aria-label={`Reorder ${name} column`}
        className={styles.handle}
    >
        <DragHandle6DotIcon />
    </Button>
);

/**
 * A column settings row: the column's name, then a drag handle (when
 * `isDraggable`), the pin menu for shown columns, and the eye. Required and
 * immutable columns get a disabled eye; immutable ones also lose the menu.
 * Callers decide what moves: the table gear keeps immutable columns in place,
 * while the spreadsheet gear's immutable columns only can't be hidden.
 */
export const ColumnItem = ({
    id,
    name,
    hint,
    isVisible,
    isPinned,
    immutable = false,
    required = false,
    onToggleVisibility,
    onPin,
    isDraggable = true,
    isDragInProgress = false,
}: ColumnItemProps) => {
    const transition = useColumnReorderTransition();
    const isLocked = immutable || required;
    const sortable = useSortable({
        id,
        disabled: !isDraggable,
        transition,
    });

    return (
        <ColumnRow
            ref={sortable.setNodeRef}
            style={{
                transform: CSS.Transform.toString(sortable.transform),
                transition: sortable.transition,
            }}
            className={classNames({
                [styles.dragging]: sortable.isDragging,
                [styles.dragInProgress]: isDragInProgress,
            })}
            name={name}
            hint={hint}
            isVisible={isVisible}
            isPinned={isPinned}
            isLocked={isLocked}
            isImmutable={immutable}
            onToggleVisibility={onToggleVisibility}
            onPin={onPin}
            handle={
                isDraggable ? (
                    <ColumnDragHandle name={name} sortable={sortable} />
                ) : null
            }
        />
    );
};

// Set on the node: the installed React types predate the `inert` prop.
const makeInert = (node: HTMLElement | null) => {
    if (node !== null) {
        node.inert = true;
    }
};

const ignore = () => undefined;

/**
 * The row a drag carries, lifted off the list. The row takes no input; the
 * wrapper stays hit-testable so the pointer shows the grabbing cursor.
 */
export const ColumnItemOverlay = ({
    name,
    hint,
    isPinned,
    isLocked,
    hasPinMenu,
    isVisible = true,
}: Readonly<{
    name: string;
    hint?: string;
    isPinned?: ColumnPin;
    isLocked: boolean;
    hasPinMenu: boolean;
    isVisible?: boolean;
}>) => (
    <div className={styles.overlay}>
        <ColumnRow
            ref={makeInert}
            name={name}
            hint={hint}
            isVisible={isVisible}
            isPinned={isPinned}
            isLocked={isLocked}
            isImmutable={false}
            onToggleVisibility={ignore}
            onPin={hasPinMenu ? ignore : undefined}
            handle={<ColumnDragHandle name={name} />}
        />
    </div>
);
